import {
  McProcessingStatus,
  McProfileInputLabel,
  McProfileOutputLabel,
  McProfileSource,
  McUnifiedProfileQueryParams,
  McLastActivityLevel,
  McKeywordsTarget,
} from '@components/templates/employer/applicant-list/flow-mc/types';

const PROFILE_SOURCES = new Set<McProfileSource>([
  'applicant',
  'matching_candidate',
  'recommended_candidate',
  'saved_profile',
]);

const PROFILE_INPUT_LABELS = new Set<McProfileInputLabel>([
  'applicants',
  'recommended',
  'uploaded',
  'processed',
]);

const PROFILE_OUTPUT_LABELS = new Set<McProfileOutputLabel>([
  'added',
  'interview',
  'rejected',
  'shortlist',
]);

const PROCESSING_STATUSES = new Set<McProcessingStatus>([
  'PROCESSING',
  'PROCESSING_FAILED',
  'INTERVIEW_PROCESSING',
  'INTERVIEW_COMPLETED',
  'INTERVIEW_FAILED',
]);

const LAST_ACTIVITY_LEVELS = new Set<McLastActivityLevel>([
  'last-week',
  'last-month',
  'last-3-months',
  'last-6-months',
  'last-year',
]);

const KEYWORDS_TARGETS = new Set<McKeywordsTarget>([
  'name',
  'experienceCompanyNames',
  'experienceNames',
  '',
]);

const getFirstValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    const [firstValue] = value as unknown[];
    return firstValue;
  }

  return value;
};

const parseNumber = (value: unknown): number | undefined => {
  const firstValue = getFirstValue(value);
  if (typeof firstValue === 'number' && Number.isFinite(firstValue)) return firstValue;
  if (typeof firstValue === 'string' && firstValue.trim() !== '') {
    const parsedValue = Number(firstValue);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }
  return undefined;
};

const parseString = (value: unknown): string | undefined => {
  const firstValue = getFirstValue(value);
  return typeof firstValue === 'string' && firstValue.trim() !== '' ? firstValue : undefined;
};

const parseBoolean = (value: unknown): boolean | undefined => {
  const firstValue = getFirstValue(value);
  if (typeof firstValue === 'boolean') return firstValue;
  if (typeof firstValue !== 'string') return undefined;

  if (firstValue === 'true') return true;
  if (firstValue === 'false') return false;

  return undefined;
};

interface ExtractMcUnifiedProfileQueryParamsOptions {
  includeStatus?: boolean;
  includePagination?: boolean;
  includeFilters?: boolean;
}

export const extractMcUnifiedProfileQueryParams = (
  params?: Record<string, unknown> | BjSearchParams,
  options: ExtractMcUnifiedProfileQueryParamsOptions = {},
): McUnifiedProfileQueryParams => {
  const {
    includeStatus = true,
    includePagination = false,
    includeFilters = true,
  } = options;

  if (!params) return {};

  const queryParams: McUnifiedProfileQueryParams = {};
  const profileSource = parseString(params.profileSource);
  const processingStatus = parseString(params.processingStatus);
  const minReferenceDate = parseString(params.minReferenceDate);
  const minFinalScore = parseNumber(params.minFinalScore);
  const status = parseNumber(params.status);
  const statuses = parseString(params.statuses);
  const page = parseNumber(params.page);
  const perPage = parseNumber(params.perPage);
  const offset = parseNumber(params.offset);
  const withPlaceholders = parseBoolean(params.withPlaceholders);
  const profileInputLabel = parseString(params.profileInputLabel);
  const profileOutputLabel = parseString(params.profileOutputLabel);
  const profileInputLabels = parseString(params.profileInputLabels);

  // Filter params
  const newParam = parseBoolean(params.new);
  const keywords = parseString(params.keywords);
  const keywordsTarget = parseString(params.keywordsTarget);
  const withInterview = parseBoolean(params.withInterview);
  const lastActivityLevel = parseString(params.lastActivityLevel);
  const experienceLevels = parseString(params.experienceLevels);
  const studyLevels = parseString(params.studyLevels);
  const locations = parseString(params.locations);
  const spokenLanguages = parseString(params.spokenLanguages);
  const withAggregations = parseBoolean(params.withAggregations);

  if (profileSource && PROFILE_SOURCES.has(profileSource as McProfileSource)) {
    queryParams.profileSource = profileSource as McProfileSource;
  }

  if (processingStatus && PROCESSING_STATUSES.has(processingStatus as McProcessingStatus)) {
    queryParams.processingStatus = processingStatus as McProcessingStatus;
  }

  if (typeof minFinalScore === 'number') {
    queryParams.minFinalScore = minFinalScore;
  }

  if (minReferenceDate) {
    queryParams.minReferenceDate = minReferenceDate;
  }

  if (typeof withPlaceholders === 'boolean') {
    queryParams.withPlaceholders = withPlaceholders;
  }

  if (profileInputLabel && PROFILE_INPUT_LABELS.has(profileInputLabel as McProfileInputLabel)) {
    queryParams.profileInputLabel = profileInputLabel as McProfileInputLabel;
  }

  if (profileOutputLabel && PROFILE_OUTPUT_LABELS.has(profileOutputLabel as McProfileOutputLabel)) {
    queryParams.profileOutputLabel = profileOutputLabel as McProfileOutputLabel;
  }

  if (profileInputLabels) {
    queryParams.profileInputLabels = profileInputLabels;
  }

  if (includeStatus) {
    if (statuses) {
      queryParams.statuses = statuses;
    } else if (typeof status === 'number') {
      queryParams.status = status;
    }
  }

  if (includePagination) {
    if (typeof page === 'number') queryParams.page = page;
    if (typeof perPage === 'number') queryParams.perPage = perPage;
    if (typeof offset === 'number') queryParams.offset = offset;
  }

  // Filter params parsing
  if (includeFilters) {
    if (typeof newParam === 'boolean') {
      queryParams.new = newParam;
    }

    if (keywords) {
      queryParams.keywords = keywords;
    }

    if (keywordsTarget !== undefined && KEYWORDS_TARGETS.has(keywordsTarget as McKeywordsTarget)) {
      queryParams.keywordsTarget = keywordsTarget as McKeywordsTarget;
    }

    if (typeof withInterview === 'boolean') {
      queryParams.withInterview = withInterview;
    }

    if (lastActivityLevel && LAST_ACTIVITY_LEVELS.has(lastActivityLevel as McLastActivityLevel)) {
      queryParams.lastActivityLevel = lastActivityLevel as McLastActivityLevel;
    }

    if (experienceLevels) {
      queryParams.experienceLevels = experienceLevels;
    }

    if (studyLevels) {
      queryParams.studyLevels = studyLevels;
    }

    if (locations) {
      queryParams.locations = locations;
    }

    if (spokenLanguages) {
      queryParams.spokenLanguages = spokenLanguages;
    }

    if (typeof withAggregations === 'boolean') {
      queryParams.withAggregations = withAggregations;
    }
  }

  return queryParams;
};
